import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebase';
import {
    doc,
    collection,
    onSnapshot,
    addDoc,
    runTransaction,
    serverTimestamp,
    query,
    orderBy
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { fetchMarketBySlug } from '../services/marketService';

const PaperTradingContext = createContext(null);

export const usePaperTrading = () => {
    const context = useContext(PaperTradingContext);
    if (!context) {
        throw new Error('usePaperTrading must be used within a PaperTradingProvider');
    }
    return context;
};

export const PaperTradingProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [balance, setBalance] = useState(1000);
    const [positions, setPositions] = useState([]);
    const [tradeHistory, setTradeHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    // Track User Auth
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            if (!currentUser) {
                setBalance(1000);
                setPositions([]);
                setTradeHistory([]);
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    // Sync Balance & Trades from Firestore
    useEffect(() => {
        if (!user) return;

        setLoading(true);

        // 1. Subscribe to User Balance
        const userRef = doc(db, 'users', user.uid);
        const unsubUser = onSnapshot(userRef, (doc) => {
            if (doc.exists()) {
                const data = doc.data();
                setBalance(data.virtualBalance || 1000);
            }
        });

        // 2. Subscribe to Trades History
        const tradesRef = collection(db, 'users', user.uid, 'trades');
        const q = query(tradesRef, orderBy('timestamp', 'desc'));

        const unsubTrades = onSnapshot(q, (snapshot) => {
            const trades = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Separate open positions vs history if needed, or simply store all
            // For this implementation, we'll derive "active positions" 
            // In a real app, you might want a separate 'positions' subcollection for efficient querying
            // But for simple paper trading, aggregating trades is okay.

            // Logic: Group by marketId/Outcome to find net position
            const activePositions = calculatePositions(trades);

            setTradeHistory(trades);
            setPositions(activePositions);
            setLoading(false);
        });

        return () => {
            unsubUser();
            unsubTrades();
        };
    }, [user]);

    // Helper to calculate net positions from trade history
    const calculatePositions = (trades) => {
        const posMap = {};

        trades.forEach(trade => {
            const key = `${trade.marketId}-${trade.side}`; // e.g., "btc-100k-YES"

            if (!posMap[key]) {
                posMap[key] = {
                    marketId: trade.marketId,
                    marketTitle: trade.marketTitle,
                    marketSlug: trade.marketSlug,
                    side: trade.side,
                    amount: 0, // Shares or $ amount
                    invested: 0,
                    avgPrice: 0,
                    currentPrice: trade.price, // Will update later
                };
            }

            // Simple aggregation: Add amounts
            // Note: This assumes only "BUY" trades for now as per simple paper trading logic
            // If selling is implemented, would need specific logic.
            posMap[key].amount += parseFloat(trade.amount);
            posMap[key].invested += parseFloat(trade.amount);
            // Weighted average price could be calculated here
        });

        return Object.values(posMap).filter(p => p.amount > 0);
    };

    // Execute Trade (Transaction)
    const executeTrade = async (market, amount, side, outcomeParams) => {
        if (!user) throw new Error("Please login to trade");
        const tradeAmount = parseFloat(amount);

        if (tradeAmount > balance) {
            throw new Error("Insufficient balance");
        }

        const userRef = doc(db, 'users', user.uid);
        const tradesRef = collection(db, 'users', user.uid, 'trades');

        try {
            await runTransaction(db, async (transaction) => {
                const userDoc = await transaction.get(userRef);

                let currentBalance = 0;

                if (!userDoc.exists()) {
                    // Initialize if missing
                    currentBalance = 1000;
                    transaction.set(userRef, {
                        uid: user.uid,
                        email: user.email || '',
                        displayName: user.displayName || 'User',
                        photoURL: user.photoURL || '',
                        virtualBalance: 1000,
                        createdAt: serverTimestamp(),
                        lastLogin: serverTimestamp()
                    });
                } else {
                    const data = userDoc.data();
                    currentBalance = data.virtualBalance !== undefined ? data.virtualBalance : 1000;
                    if (data.virtualBalance === undefined) {
                        transaction.update(userRef, { virtualBalance: 1000 });
                    }
                }

                if (currentBalance < tradeAmount) throw new Error("Insufficient balance");

                // Deduct balance
                if (userDoc.exists() && userDoc.data().virtualBalance !== undefined) {
                    transaction.update(userRef, {
                        virtualBalance: currentBalance - tradeAmount
                    });
                } else if (userDoc.exists()) {
                    transaction.update(userRef, {
                        virtualBalance: currentBalance - tradeAmount
                    });
                } else {
                    transaction.set(userRef, {
                        uid: user.uid,
                        email: user.email || '',
                        displayName: user.displayName || 'User',
                        photoURL: user.photoURL || '',
                        virtualBalance: currentBalance - tradeAmount,
                        createdAt: serverTimestamp(),
                        lastLogin: serverTimestamp()
                    });
                }

                // Add trade record
                // We use setDoc with a new ID instead of add() inside transaction for simplicity or just create ref
                const newTradeRef = doc(collection(db, 'users', user.uid, 'trades'));
                transaction.set(newTradeRef, {
                    marketId: market.id || market.slug,
                    marketSlug: market.slug,
                    marketTitle: market.title,
                    amount: tradeAmount,
                    side: side,
                    price: outcomeParams?.price || 0.5,
                    timestamp: serverTimestamp(),
                    status: 'OPEN',
                    type: 'BUY',
                    ...outcomeParams
                });
            });

            return { status: 'success' };
        } catch (error) {
            console.error("Trade failed:", error);
            throw error;
        }
    };

    // Reset Account
    const resetAccount = async () => {
        if (!user) return;

        if (window.confirm("Are you sure? This will convert your balance back to $1000 and wipe trade history.")) {
            try {
                // Not a true atomic wipe of subcollections (Firestore limitation), 
                // but we can reset balance and let user start fresh.
                // Deleting all trades in subcollection requires persistent cloud function or client-side loop.
                // For MVP: Reset balance to 1000.
                await runTransaction(db, async (transaction) => {
                    const userRef = doc(db, 'users', user.uid);
                    transaction.update(userRef, { virtualBalance: 1000 });
                });
                // TODO: Ideally delete subcollection 'trades'
                // For now, UI might show old trades but balance is reset. 
                // Or we can filter trades by 'resetDate' in user profile.
            } catch (error) {
                console.error("Reset failed:", error);
            }
        }
    };

    // Refresh Portfolio Prices (Real-time tracking of value)
    const refreshPortfolio = useCallback(async () => {
        if (positions.length === 0) return;

        // Fetch latest prices for all markets in positions
        const updatedPositions = await Promise.all(positions.map(async (pos) => {
            try {
                const marketData = await fetchMarketBySlug(pos.marketSlug);
                // Find updated price for the side (YES/NO)
                const outcome = marketData.outcomes.find(o => o.name.toUpperCase() === pos.side.toUpperCase());
                const currentPrice = outcome ? outcome.price : pos.price;

                return {
                    ...pos,
                    currentPrice: currentPrice,
                    currentValue: (pos.amount / pos.price) * currentPrice, // Approx mechanism if taking 'amount' as $ invested
                    // If 'amount' was shares: currentPrice * pos.amount
                    // Assuming 'amount' in executeTrade is USD invested for this MVP:
                    // Shares = Invested / EntryPrice
                    // CurrentValue = Shares * CurrentPrice
                    shares: pos.invested / (pos.avgPrice || 0.5) // distinct from 'price' field? uses current pos logic
                };
            } catch (e) {
                return pos;
            }
        }));

        // Note: We don't save ephemeral price updates to Firestore to save writes.
        // We just update local state for the UI.
        // But wait, 'positions' state is derived from Firestore snapshot. 
        // We should store these 'enriched' positions in a separate state or 
        // merge them.

        // Better approach: use a separate 'prices' map state or 'portfolioValue' state.
        setPositions(prev => {
            return prev.map(p => {
                const updated = updatedPositions.find(up => up.marketId === p.marketId && up.side === p.side);
                return updated || p;
            });
        });

    }, [positions]); // Be careful of infinite loops

    const totalEquity = balance + positions.reduce((acc, pos) => {
        // Calculate based on updated currentPrice
        // Logic: You bought $X at Price P1. You have X/P1 shares.
        // Value = (X/P1) * CurrentPrice
        const entryPrice = pos.price || 0.5; // fallback
        const currentPrice = pos.currentPrice || entryPrice;
        const shares = pos.amount / entryPrice;
        const value = shares * currentPrice;
        return acc + value;
    }, 0);

    const value = {
        balance,
        positions,
        tradeHistory,
        loading,
        totalEquity,
        executeTrade,
        resetAccount,
        refreshPortfolio
    };

    return (
        <PaperTradingContext.Provider value={value}>
            {children}
        </PaperTradingContext.Provider>
    );
};
